import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CalendarPlus, Camera, Edit, MessageCircle, Phone, Save, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api, withAuthToken } from '@/lib/api';
import { useClient, useTags } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { fmtMoney } from '@/lib/format';
import { hapticNotify, tgConfirm } from '@/lib/telegram';
import { Avatar } from '@/components/ui/avatar';
import { PageHeader } from '@/components/PageHeader';

export const ClientDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: c, isLoading, isError, error, refetch } = useClient(id);
  const { data: tags = [] } = useTags();

  if (isError) {
    return (
      <div className="space-y-3 p-2">
        <Card>
          <CardContent className="space-y-3 p-4 text-sm">
            <div className="font-medium text-destructive">
              {(error as any)?.response?.data?.error ?? 'Не удалось загрузить клиента'}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => refetch()} className="flex-1">
                Повторить
              </Button>
              <Button variant="ghost" onClick={() => nav(-1)} className="flex-1">
                Назад
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (isLoading || !c) {
    return (
      <div className="space-y-3 p-2">
        <div className="skeleton h-10 w-48" />
        <div className="skeleton h-40 w-full" />
        <div className="skeleton h-11 w-full" />
        <div className="skeleton h-32 w-full" />
      </div>
    );
  }

  const stats = useStats(c.appointments ?? []);

  const onDelete = async () => {
    if (!(await tgConfirm('Удалить клиента?'))) return;
    try {
      await api.delete(`/clients/${id}`);
      qc.invalidateQueries({ queryKey: ['clients'] });
      nav('/clients');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Ошибка');
    }
  };

  const upload = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    await api.post(`/clients/${id}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    refetch();
  };

  return (
    <div className="animate-in-up space-y-4 pb-2">
      <PageHeader title="Профиль клиента" />

      <Card className="bg-aurora-card overflow-hidden">
        <CardContent className="space-y-3 p-4">
          <div className="flex items-start gap-3">
            <Avatar src={c.photo_url} name={c.name} size="xl" />
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-2xl font-bold tracking-tight">{c.name}</h1>
              <div className="mt-1 flex flex-col gap-0.5">
                {c.phone && (
                  <a
                    href={`tel:${c.phone}`}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
                  >
                    <Phone className="h-3.5 w-3.5" /> {c.phone}
                  </a>
                )}
                {c.telegram_username && (
                  <a
                    href={`https://t.me/${c.telegram_username.replace('@', '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> @
                    {c.telegram_username.replace('@', '')}
                  </a>
                )}
              </div>
            </div>
          </div>
          {!!c.tags?.length && (
            <div className="flex flex-wrap gap-1.5">
              {c.tags.map((t: any) => (
                <span
                  key={t.id}
                  className="rounded-full px-2.5 py-1 text-[10px] font-bold"
                  style={{ background: `${t.color}1f`, color: t.color }}
                >
                  {t.name}
                </span>
              ))}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <Stat label="Визитов" value={String(stats.count)} />
            <Stat label="Сумма" value={fmtMoney(stats.total)} small />
            <Stat label="Ср. чек" value={fmtMoney(stats.avg)} small />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-2">
        <Link to={`/appointments/new?client=${id}`}>
          <Button className="w-full">
            <CalendarPlus className="h-4 w-4" /> Записать
          </Button>
        </Link>
        <EditClientDialog client={c} tags={tags} onSaved={refetch} />
      </div>
      <ScheduleCorrection client={c} />

      <Tabs defaultValue="history">
        <TabsList>
          <TabsTrigger value="info">Инфо</TabsTrigger>
          <TabsTrigger value="history">История</TabsTrigger>
          <TabsTrigger value="notes">Заметки</TabsTrigger>
          <TabsTrigger value="photos">Фото</TabsTrigger>
          <TabsTrigger value="finance">Финансы</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card>
            <CardContent className="space-y-3 pt-4 text-sm">
              {c.birthday && <Row label="День рождения" value={new Date(c.birthday).toLocaleDateString('ru-RU')} />}
              {c.source && <Row label="Источник" value={c.source} />}
              {c.hair_type && <Row label="Тип волос" value={c.hair_type} />}
              {c.note && <div><Label>Заметка</Label><div className="mt-1 whitespace-pre-wrap">{c.note}</div></div>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <div className="space-y-2">
            {(c.appointments ?? []).map((a: any) => (
              <Link key={a.id} to={`/appointments/${a.id}`}>
                <Card className="transition-all hover:-translate-y-0.5 hover:shadow-soft-md">
                  <CardContent className="space-y-1 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">
                        {new Date(a.date).toLocaleDateString('ru-RU')}
                      </span>
                      <span className="text-sm font-bold tabular">
                        {fmtMoney(
                          Number(a.work_price) +
                            Number(a.hair_price) +
                            Number(a.tangle_price ?? 0) +
                            (a.extras ?? []).reduce(
                              (s: number, e: any) => s + Number(e.price),
                              0,
                            ),
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: a.service?.color }}
                      />
                      {a.service?.name}
                      {a.strands_count ? ` · ${a.strands_count} пр.` : ''}
                      {a.hair_grams ? ` · ${a.hair_grams} г` : ''}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
            {!c.appointments?.length && (
              <Card>
                <CardContent className="p-8 text-center text-sm text-muted-foreground">
                  История пуста
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="notes">
          <NotesTab client={c} onSaved={refetch} />
        </TabsContent>

        <TabsContent value="photos">
          <Card>
            <CardContent className="space-y-3 pt-4">
              <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/50 p-3 transition-colors hover:border-primary hover:bg-primary-soft/40">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-card text-primary">
                  <Camera className="h-4 w-4" />
                </span>
                <span className="flex-1 text-sm font-medium">Добавить фото</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                  className="hidden"
                />
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(c.photos ?? []).map((p: any) => (
                  <a key={p.id} href={withAuthToken(p.url)} target="_blank" rel="noreferrer">
                    <img
                      src={withAuthToken(p.url)}
                      alt=""
                      className="aspect-square w-full rounded-xl object-cover transition-transform hover:scale-[1.02]"
                    />
                  </a>
                ))}
                {!c.photos?.length && (
                  <div className="col-span-3 py-4 text-center text-xs text-muted-foreground">
                    Пока нет фото
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="finance">
          <Card>
            <CardContent className="space-y-2 pt-4 text-sm">
              <Row label="Выручка за работу" value={fmtMoney(stats.work)} />
              <Row label="Выручка за волосы" value={fmtMoney(stats.hair)} />
              <Row label="Выручка за доп. услуги" value={fmtMoney(stats.extras)} />
              <div className="my-2 border-t border-border/60" />
              <Row label="Всего" value={fmtMoney(stats.total)} bold />
              <Row label="Средний чек" value={fmtMoney(stats.avg)} />
              {stats.debt > 0 && (
                <Row label="Долг" value={fmtMoney(stats.debt)} className="text-warning" />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Button
        variant="ghost"
        onClick={onDelete}
        className="w-full text-destructive hover:bg-destructive-soft"
      >
        <Trash2 className="h-4 w-4" /> Удалить клиента
      </Button>
    </div>
  );
};

const Stat = ({ label, value, small }: { label: string; value: string; small?: boolean }) => (
  <div className="rounded-xl bg-card/60 p-2.5 text-center backdrop-blur">
    <div className={`font-bold tabular ${small ? 'text-sm' : 'text-xl'}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);

const Row = ({
  label,
  value,
  bold,
  className,
}: {
  label: string;
  value: string;
  bold?: boolean;
  className?: string;
}) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span
      className={`${bold ? 'text-base font-bold' : 'font-semibold'} tabular ${className ?? ''}`}
    >
      {value}
    </span>
  </div>
);

const useStats = (apps: any[]) => {
  const completed = apps.filter((a) => a.status === 'completed');
  const work = completed.reduce((s, a) => s + Number(a.work_price), 0);
  const hair = completed.reduce((s, a) => s + Number(a.hair_price), 0);
  const tangle = completed.reduce((s, a) => s + Number(a.tangle_price ?? 0), 0);
  const extras = completed.reduce(
    (s, a) => s + (a.extras ?? []).reduce((ss: number, e: any) => ss + Number(e.price), 0),
    0,
  );
  const total = work + hair + tangle + extras;
  const debt = completed.reduce((s, a) => s + Number(a.debt_amount ?? 0), 0);
  const count = completed.length;
  return { count, total, work, hair, tangle, extras, avg: count ? total / count : 0, debt };
};

const ScheduleCorrection = ({ client }: { client: any }) => {
  const last = (client.appointments ?? [])
    .filter((a: any) => a.status === 'completed')
    .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  const nav = useNavigate();
  const weeks = [4, 6, 8, 12];

  const go = (w: number) => {
    const d = last ? new Date(last.date) : new Date();
    d.setDate(d.getDate() + w * 7);
    const date = d.toISOString().slice(0, 10);
    const params = new URLSearchParams({ client: client.id, date });
    if (last?.service?.id) params.set('service', last.service.id);
    nav(`/appointments/new?${params.toString()}`);
  };

  return (
    <Card>
      <CardContent className="space-y-2 pt-3">
        <div className="text-sm font-medium">Запланировать коррекцию</div>
        <div className="grid grid-cols-4 gap-2">
          {weeks.map((w) => (
            <Button key={w} variant="outline" size="sm" onClick={() => go(w)}>
              {w} нед
            </Button>
          ))}
        </div>
        {last && (
          <div className="text-xs text-muted-foreground">
            от {new Date(last.date).toLocaleDateString('ru-RU')} ({last.service?.name})
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const NotesTab = ({ client, onSaved }: { client: any; onSaved: () => void }) => {
  const [note, setNote] = useState(client.note ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Re-sync local state when the underlying client record changes (refetch).
  useEffect(() => {
    setNote(client.note ?? '');
  }, [client.id, client.note]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}`, { note });
      hapticNotify('success');
      setSaved(true);
      onSaved();
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-2 pt-4">
        <Label>Заметки о клиенте</Label>
        <Textarea
          rows={10}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Особенности, предпочтения, что обсудили..."
        />
        <Button onClick={save} disabled={saving || note === (client.note ?? '')} className="w-full">
          <Save className="h-4 w-4" /> {saved ? 'Сохранено' : 'Сохранить'}
        </Button>
      </CardContent>
    </Card>
  );
};

const EditClientDialog = ({ client, tags, onSaved }: { client: any; tags: any[]; onSaved: () => void }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(client.name);
  const [phone, setPhone] = useState(client.phone ?? '');
  const [tg, setTg] = useState(client.telegram_username ?? '');
  const [bday, setBday] = useState(client.birthday ? client.birthday.slice(0, 10) : '');
  const [source, setSource] = useState(client.source ?? '');
  const [hairType, setHairType] = useState(client.hair_type ?? '');
  const [note, setNote] = useState(client.note ?? '');
  const [tagIds, setTagIds] = useState<string[]>(client.tags?.map((t: any) => t.id) ?? []);

  const save = async () => {
    await api.patch(`/clients/${client.id}`, {
      name,
      phone: phone || undefined,
      telegram_username: tg || undefined,
      birthday: bday || undefined,
      source: source || undefined,
      hair_type: hairType || undefined,
      note: note || undefined,
      tag_ids: tagIds,
    });
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full"><Edit className="h-4 w-4" /> Редактировать</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Редактировать клиента</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Имя</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>Телефон</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
          <div><Label>Telegram</Label><Input value={tg} onChange={(e) => setTg(e.target.value)} /></div>
          <div><Label>День рождения</Label><Input type="date" value={bday} onChange={(e) => setBday(e.target.value)} /></div>
          <div><Label>Источник</Label><Input value={source} onChange={(e) => setSource(e.target.value)} /></div>
          <div><Label>Тип волос</Label><Textarea rows={2} value={hairType} onChange={(e) => setHairType(e.target.value)} /></div>
          <div><Label>Заметка</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <div>
            <Label>Теги</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tags.map((t: any) => {
                const on = tagIds.includes(t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setTagIds((p) => (on ? p.filter((x) => x !== t.id) : [...p, t.id]))}
                    className="rounded-full border px-3 py-1 text-xs"
                    style={on ? { background: t.color, color: '#fff', borderColor: t.color } : { color: t.color, borderColor: t.color }}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={save} className="w-full">Сохранить</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
